import * as Sentry from '@sentry/react';
import type React from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/core/providers/AuthFirebaseProvider';
import type { Department } from '@/generated/graphql-types';
import { useGetCurrentUserDepartments } from './query/common.hooks';

const DEFAULT_DEPARTMENT_ID = 2;

interface DepartmentsContextValue {
  departments: Partial<Department>[];
  selectedDepartment: Partial<Department> | undefined;
  selectedDepartmentId: number;
  setSelectedDepartmentId: (departmentId: number) => void;
  isLoading: boolean;
  allDepartmentIds: number[];
}

const DepartmentsContext = createContext<DepartmentsContextValue | undefined>(undefined);

export const DepartmentsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userDataParams } = useAuth();
  const { data: departmentsData, isLoading } = useGetCurrentUserDepartments({
    userId: userDataParams?.userId ?? '',
  });

  const departments = useMemo<Partial<Department>[]>(() => {
    if (!departmentsData?.departments_users) return [];
    return departmentsData.departments_users.map((du) => du.department);
  }, [departmentsData]);

  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number>(DEFAULT_DEPARTMENT_ID);

  useEffect(() => {
    if (departments.length === 0) return;

    const storedDepartmentId = localStorage.getItem('choosed_department');
    const defaultDepartment = departments.find((dept) => {
      return storedDepartmentId ? dept.id === Number.parseInt(storedDepartmentId, 10) : false;
    });

    if (defaultDepartment?.id !== undefined) {
      setSelectedDepartmentId(defaultDepartment.id);
    } else if (departments[0]?.id !== undefined) {
      setSelectedDepartmentId(departments[0].id);
    }
  }, [departments]);

  const updateSelectedDepartment = (departmentId: number) => {
    setSelectedDepartmentId(departmentId);
    localStorage.setItem('choosed_department', departmentId.toString());
  };

  const selectedDepartment = departments.find((d) => d.id === selectedDepartmentId);
  const allDepartmentIds = departments
    .map((d) => d.id)
    .filter((id): id is number => id !== undefined);

  useEffect(() => {
    if (!Sentry.isInitialized()) return;
    if (allDepartmentIds.length === 0) {
      Sentry.setContext('departments', null);
      return;
    }
    Sentry.setContext('departments', {
      ids: allDepartmentIds.join(', '),
      selected: selectedDepartmentId,
    });
  }, [allDepartmentIds, selectedDepartmentId]);

  return (
    <DepartmentsContext.Provider
      value={{
        departments,
        selectedDepartment,
        selectedDepartmentId,
        setSelectedDepartmentId: updateSelectedDepartment,
        isLoading,
        allDepartmentIds,
      }}
    >
      {children}
    </DepartmentsContext.Provider>
  );
};

export const useDepartments = (): DepartmentsContextValue => {
  const context = useContext(DepartmentsContext);
  if (!context) {
    throw new Error('useDepartments must be used within a DepartmentsProvider');
  }
  return context;
};
